import { useState, useMemo, useCallback } from 'react';

// From/To filtering over a date field. Cemetery, Meetings, Assets, HR and the
// history modules all repeat this.
//
//   const range = useDateRangeFilter(rows, (r) => r.date || r.dateAdded);
//   <DateRangeFilter {...range} />   // then use range.filtered

export interface UseDateRangeFilterResult<T> {
  startDate: string;
  setStartDate: (v: string) => void;
  endDate: string;
  setEndDate: (v: string) => void;
  filtered: T[];
  clear: () => void;
  isActive: boolean;
}

export const useDateRangeFilter = <T = unknown>(
  items: T[] | undefined,
  getDate: (item: T) => string | undefined = (item) => (item as { date?: string })?.date
): UseDateRangeFilterResult<T> => {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const filtered = useMemo<T[]>(() => {
    const list = Array.isArray(items) ? items : [];
    if (!startDate && !endDate) return list;

    const atMidnight = (value: string): Date | null => {
      const d = new Date(value);
      if (isNaN(d.getTime())) return null;
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const from = startDate ? atMidnight(startDate) : null;
    const to = endDate ? atMidnight(endDate) : null;

    return list.filter((item) => {
      const raw = getDate(item);
      if (!raw) return true; // undated rows are never filtered out
      const value = atMidnight(raw);
      if (!value) return true;
      if (from && value < from) return false;
      if (to && value > to) return false;
      return true;
    });
  }, [items, getDate, startDate, endDate]);

  const clear = useCallback(() => { setStartDate(''); setEndDate(''); }, []);
  const isActive = Boolean(startDate || endDate);

  return { startDate, setStartDate, endDate, setEndDate, filtered, clear, isActive };
};

export default useDateRangeFilter;
